class Particle {
    public options: any
    public radius: number
    public dynamicRadius: number
    public x: number
    public y: number
    public vx: number = 0
    public vy: number = 0
    public dx: number = 0
    public dy: number = 0
    public ex: number = 0
    public ey: number = 0
    public time: number = 0
    public state: string = 'move'
    public opacity: number = 1
    public vo: number = 0

    constructor (options: any) {
        const { x, y, radius, w, h, duration } = options
        this.options = options
        this.x = x
        this.y = y
        this.radius = radius
        this.dynamicRadius = radius
        this.dx = Math.floor(Math.random() * (w - 2 * radius))
        this.dy = Math.floor(Math.random() * (h - 2 * radius))
        this.vx = Math.abs(this.dx - x) / duration
        this.vy = Math.abs(this.dy - y) / duration
        this.time = new Date().getTime()
    }

    update () {
        if (this.dx === this.x && this.dy === this.y && this.state !== 'destroy') {
            const dynamicRadius = this.radius + 2 * Math.sin(new Date().getTime() / 2500 % 2500 * this.radius)
            this.dynamicRadius = Math.max(1, dynamicRadius)
            return
        }
        const sizeTime = new Date().getTime() - this.time
        this.time = new Date().getTime()
        if (this.state === 'destroy') {
            this.opacity -= sizeTime * this.vo * 1.5
        }
        let x = this.x
        let y = this.y
        if (this.dx !== x) {
            const vx = sizeTime * this.vx
            if (this.dx > x) {
                this.dx -= vx
                this.dx = Math.max(x, this.dx)
            } else {
                this.dx += vx
                this.dx = Math.min(x, this.dx)
            }
        }
        if (this.dy !== y) {
            const vy = sizeTime * this.vy
            if (this.dy > y) {
                this.dy -= vy
                this.dy = Math.max(y, this.dy)
            } else {
                this.dy += vy
                this.dy = Math.min(y, this.dy)
            }
        }
    }

    draw (ctx) {
        ctx.beginPath()
        ctx.arc(this.dx, this.dy, this.dynamicRadius, 0, 2 * Math.PI, false)
        ctx.fillStyle = this.options.color
        ctx.globalAlpha = this.opacity
        ctx.fill()
        ctx.closePath()
    }

    destroy () {
        const { w, h, duration, radius } = this.options
        const dir = Math.random() > .5 ? 'x' : 'y'
        this.x = dir === 'x' ? Math.random() > .5 ? 0 : w : Math.floor(Math.random() * (w + 2 * radius))
        this.y = dir === 'y' ? Math.random() > .5 ? 0 : h : Math.floor(Math.random() * (w + 2 * radius))
        this.vx = Math.abs(this.dx - this.x) / duration
        this.vy = Math.abs(this.dy - this.y) / duration
        this.vo = 1 / duration
        this.state = 'destroy'
        this.time = new Date().getTime()
    }
}

export default class PicParticles {
    public options: any
    public container: any
    public pix: number
    public radius: number
    public color: string
    public dynamicRadius: number
    public canvas: any
    public ctx: any
    public particles: any = []
    public animateId: number = 0
    public state: string = 'move'

    constructor (options: any) {
        const { container, pix, color, radius, imgUrl } = options
        this.options = options
        this.container = container
        this.pix = Math.floor(pix / 2) * 2
        this.radius = radius || pix
        this.color = color
        this.dynamicRadius = radius
        this.loadImage(imgUrl).then((img) => {
            this.init(img)
        })
    }

    init (img: any) {
        this.canvas = document.createElement('canvas')
        this.ctx = this.canvas.getContext('2d', { willReadFrequently: true })
        this.container.appendChild(this.canvas)
        this.canvas.width = img.width
        this.canvas.height = img.height
        this.ctx.drawImage(img, 0, 0)
        this.splitImage(img)
        this.animate()
    }

    loadImage (url: string) {
        return new Promise((resolve) => { 
            const img = new Image()
            img.crossOrigin = 'Anonymous' 
            img.src = url
            img.addEventListener('load', () => {
                resolve(img)
            })
        })
    }

    splitImage (img) {
        let w = img.width
        let h = img.height
        const imageData = this.ctx.getImageData(0, 0, w, h)
        const pixels = imageData.data
        let cols = Math.floor(w / this.pix) + 1
        let rows = Math.floor(h / this.pix) + 1
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                let y = (row - 0.5) * this.pix
                let pixelY = Math.max(Math.min(y, h - 1), 0)
                let x = (col - 0.5) * this.pix
                let pixelX = Math.max(Math.min(x, w - 1), 0)
                let pixelIndex = (pixelX + pixelY * w) * 4
                if (pixels[pixelIndex + 3] > 0) {
                    const curColor = `rgba(${pixels[pixelIndex]}, ${pixels[pixelIndex + 1]}, ${pixels[pixelIndex + 2]}, ${pixels[pixelIndex + 3] / 255})`
                    const color = this.color ? this.color[Math.floor(Math.random() * this.color.length)] : curColor
                    this.particles.push(new Particle({
                        x: x,
                        y: y,
                        radius: this.radius * Math.sqrt(2) / 2 * this.getRadiusSize(pixels, pixelIndex),
                        color,
                        w,
                        h,
                        duration: this.options.duration / 2 + Math.random() * this.options.duration / 2
                    }))
                }
            }
        }
    }

    getRadiusSize = (pixels, pixelIndex: number) => {
        var r = pixels[pixelIndex + 0] / 255
        var g = pixels[pixelIndex + 1] / 255
        var b = pixels[pixelIndex + 2] / 255
        var max = Math.max( r, g, b )
        var min = Math.min( r, g, b )
        return 1 - ( max + min ) / 2
    }
    

    animate () {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        this.particles.forEach(particle => {
            particle.update()
            particle.draw(this.ctx)
        })
        cancelAnimationFrame(this.animateId)
        this.animateId = requestAnimationFrame(this.animate.bind(this))
    }

    forceDestroy () {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        cancelAnimationFrame(this.animateId)
        this.container.removeChild(this.canvas)
    }

    destroy () {
        this.state = 'destroy'
        this.particles.forEach(particle => {
            particle.destroy()
        })
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                this.forceDestroy()
                resolve('')
            }, this.options.duration)
        })
    }
}
